<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { domToBlob } from 'modern-screenshot'
import * as Sentry from '@sentry/vue'
import { useUserStore } from '@/stores/userStore'
import { useRootPageRef } from '@/lib/utils'
import { Textarea } from '@/components/ui/textarea'
import { Toggle } from '@/components/ui/toggle'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Icon } from '#components'

const props = defineProps<{
  onSubmit?: (success: boolean) => void
}>()

const userStore = useUserStore()
const { rootPageRef } = useRootPageRef()

// Form state
const name = ref('')
const email = ref('')
const message = ref('')
const includeScreenshot = ref(false)
const includeReplay = ref(true)

// UI state
const isSubmitting = ref(false)
const isCapturingScreenshot = ref(false)
const submitError = ref('')

// Auto-populate user fields
onMounted(() => {
  if (userStore.user) {
    name.value = (userStore.user as any)?.name || (userStore.user as any)?.email || ''
    email.value = (userStore.user as any)?.email || ''
  }
})

// Validation
const isFormValid = computed(() => {
  return message.value.trim().length > 0
})

// Screenshot capture
const captureScreenshot = async (): Promise<string | null> => {
  if (!rootPageRef.value) {
    console.warn('Root page ref not available for screenshot')
    return null
  }

  try {
    isCapturingScreenshot.value = true
    
    // Hide modal/dialog elements during screenshot
    const elementsToHide = [
      '[vaul-drawer]',
      '[data-vaul-drawer]',
      '[data-state="open"][data-radix-dialog-overlay]',
      '[data-state="open"][role="dialog"]',
      '[data-sonner-toaster]',
      '.notivue',
      '[data-state="open"][role="alertdialog"]'
    ]
    
    const hiddenElements: Array<{ element: HTMLElement; originalDisplay: string }> = []
    
    for (const selector of elementsToHide) {
      const elements = document.querySelectorAll(selector)
      elements.forEach(el => {
        const htmlEl = el as HTMLElement
        if (htmlEl.style.display !== 'none') {
          hiddenElements.push({
            element: htmlEl,
            originalDisplay: htmlEl.style.display
          })
          htmlEl.style.display = 'none'
        }
      })
    }
    
    // Small delay to ensure DOM is updated
    await new Promise(resolve => setTimeout(resolve, 100))
    
    const blob = await domToBlob(rootPageRef.value, {
      quality: 0.8,
      scale: 1,
      filter: (node: Node) => {
        if (node.nodeType !== Node.ELEMENT_NODE) return true
        
        const element = node as Element
        const tagName = element.tagName?.toLowerCase()
        const role = element.getAttribute?.('role')
        const state = element.getAttribute?.('data-state')
        
        if (role === 'dialog' || role === 'alertdialog') return false
        if (state === 'open' && element.hasAttribute('data-radix-dialog-overlay')) return false
        if (element.hasAttribute('vaul-drawer')) return false
        
        return true
      }
    })
    
    // Restore hidden elements
    hiddenElements.forEach(({ element, originalDisplay }) => {
      element.style.display = originalDisplay
    })
    
    if (!blob || blob.size === 0) {
      console.error('Invalid blob or empty blob received')
      return null
    }
    
    // Convert blob to base64
    const base64Data = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => {
        const result = reader.result as string
        const base64 = result.split(',')[1]
        resolve(base64)
      }
      reader.onerror = reject
      reader.readAsDataURL(blob)
    })
    
    console.log('Screenshot captured for Sentry feedback:', {
      blobSize: blob.size,
      base64Length: base64Data.length
    })
    
    return base64Data
  } catch (error) {
    console.error('Error capturing screenshot:', error)
    return null
  } finally {
    isCapturingScreenshot.value = false
  }
}

// Submit feedback
const submitFeedback = async () => {
  if (!isFormValid.value) return
  
  try {
    isSubmitting.value = true
    submitError.value = ''
    
    // Set user context in Sentry
    Sentry.setUser({
      email: email.value,
      name: name.value,
      id: (userStore.user as any)?.id
    })
    
    // Prepare feedback data
    const feedbackData = {
      message: message.value.trim(),
      name: name.value.trim() || 'Anonymous',
      email: email.value.trim()
    }
    
    // Prepare hints object
    const hints: any = {
      includeReplay: includeReplay.value,
      tags: {
        source: 'custom-sentry-widget',
        userAgent: navigator.userAgent,
        url: window.location.href,
        timestamp: new Date().toISOString()
      }
    }
    
    // Capture screenshot if requested
    if (includeScreenshot.value) {
      const screenshotData = await captureScreenshot()
      if (screenshotData) {
        hints.attachments = [{
          filename: `feedback-screenshot-${Date.now()}.png`,
          data: screenshotData
        }]
        console.log('Screenshot attached to Sentry feedback')
      } else {
        console.warn('Screenshot capture failed, sending feedback without attachment')
      }
    }
    
    console.log('Sending feedback to Sentry:', { feedbackData, hints })
    
    // Send to Sentry
    Sentry.captureFeedback(feedbackData, hints)
    
    // Success feedback
    console.log('Feedback sent successfully to Sentry!')
    props.onSubmit?.(true)
    
  } catch (error) {
    console.error('Failed to submit feedback:', error)
    submitError.value = 'Failed to send feedback. Please try again.'
    props.onSubmit?.(false)
  } finally {
    isSubmitting.value = false
  }
}

// Cancel and close
const cancel = () => {
  props.onSubmit?.(false)
}
</script>

<template>
  <Card class="w-full max-w-md mx-auto bg-white">
    <CardHeader>
      <CardTitle class="flex items-center gap-2">
        <Icon name="lucide:message-circle" class="w-5 h-5" />
        Share Your Feedback
      </CardTitle>
      <CardDescription>
        Help us improve SuperSeeded by sharing your thoughts, reporting bugs, or suggesting features.
      </CardDescription>
    </CardHeader>
    
    <CardContent class="space-y-4">
      <!-- Name Field -->
      <div class="space-y-2">
        <Label for="feedback-name">Your Name</Label>
        <Input
          id="feedback-name"
          v-model="name"
          placeholder="Enter your name"
          :disabled="isSubmitting"
        />
      </div>
      
      <!-- Email Field -->
      <div class="space-y-2">
        <Label for="feedback-email">Email Address</Label>
        <Input
          id="feedback-email"
          v-model="email"
          type="email"
          placeholder="your.email@example.com"
          :disabled="isSubmitting"
        />
      </div>
      
      <!-- Message Field -->
      <div class="space-y-2">
        <Label for="feedback-message" class="text-sm font-medium">
          Tell us what's on your mind *
        </Label>
        <Textarea
          id="feedback-message"
          v-model="message"
          placeholder="Describe your experience, report a bug, or suggest an improvement..."
          class="min-h-[100px] resize-none"
          :disabled="isSubmitting"
          required
        />
      </div>
      
      <!-- Options -->
      <div class="space-y-3 pt-2">
        <!-- Screenshot Toggle -->
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-2">
            <Icon 
              :name="isCapturingScreenshot ? 'lucide:loader-2' : 'lucide:camera'" 
              :class="['w-4 h-4', { 'animate-spin': isCapturingScreenshot }]" 
            />
            <Label for="screenshot-toggle" class="text-sm">
              Include Screenshot
            </Label>
          </div>
          <Toggle
            id="screenshot-toggle"
            v-model:pressed="includeScreenshot"
            :disabled="isSubmitting || isCapturingScreenshot"
            size="sm"
          />
        </div>
        
        <!-- Session Replay Toggle -->
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-2">
            <Icon name="lucide:video" class="w-4 h-4" />
            <Label for="replay-toggle" class="text-sm">
              Include Session Replay
            </Label>
          </div>
          <Toggle
            id="replay-toggle"
            v-model:pressed="includeReplay"
            :disabled="isSubmitting"
            size="sm"
          />
        </div>
      </div>
      
      <!-- Error Message -->
      <div v-if="submitError" class="text-sm text-red-500 bg-red-50 dark:bg-red-950/20 p-3 rounded-md">
        {{ submitError }}
      </div>
      
      <!-- Action Buttons -->
      <div class="flex gap-3 pt-4">
        <Button
          @click="cancel"
          variant="outline"
          class="flex-1"
          :disabled="isSubmitting"
        >
          Cancel
        </Button>
        <Button
          @click="submitFeedback"
          :disabled="!isFormValid || isSubmitting || isCapturingScreenshot"
          class="flex-1"
        >
          <Icon 
            v-if="isSubmitting" 
            name="lucide:loader-2" 
            class="w-4 h-4 mr-2 animate-spin" 
          />
          <Icon 
            v-else
            name="lucide:send" 
            class="w-4 h-4 mr-2" 
          />
          {{ isSubmitting ? 'Sending...' : 'Send Feedback' }}
        </Button>
      </div>
      
      <!-- Powered by Sentry notice -->
      <div class="text-xs text-muted-foreground text-center pt-2">
        Powered by Sentry • Your privacy is protected
      </div>
    </CardContent>
  </Card>
</template> 

<style>

@import '@/assets/css/sentry-feedback.css';


</style>
